/**
 * SEO Sprint 2 (Feb 2026) — Destination pages admin sub-section.
 *
 * Mirrors the ExtraPages / MiscPages / InfoPages editors but adds two
 * list-editable sections (`service_blocks.items`, `faq.items`) plus a
 * dedicated `seo` (title / description / keywords) sub-tab.
 *
 * Backend: /api/admin/destinations/{slug}/{section}
 *
 * Editor UX rules (PRD R4): all wrapper sub-components live at module
 * top-level → no focus-loss while typing. Controlled inputs default to
 * "" so React never sees undefined.
 */
import React, { useCallback, useEffect, useState } from "react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { toast } from "sonner";
import { Save, Plus, Trash2, Loader2, MapPin } from "lucide-react";

const SLUGS = [
  { value: "curacao",       label: "Curaçao" },
  { value: "aruba",         label: "Aruba" },
  { value: "bonaire",       label: "Bonaire" },
  { value: "sint-maarten",  label: "Sint Maarten" },
  { value: "suriname",      label: "Suriname" },
];

const ICON_OPTIONS = [
  "ship", "plane", "package", "truck", "car", "map", "briefcase", "user",
  "check", "clock", "shield", "banknote", "support", "info", "receipt", "zap",
];

const SECTIONS = [
  { key: "page_banner",     label: "Page banner" },
  { key: "intro",           label: "Intro" },
  { key: "service_blocks",  label: "Service-blokken" },
  { key: "process_steps",   label: "Stappenplan" },
  { key: "trust_signals",   label: "Trust signals" },
  { key: "faq",             label: "FAQ" },
  { key: "cta_band",        label: "CTA-band" },
  { key: "seo",             label: "SEO" },
];

function sectionLabel(key) {
  return (SECTIONS.find((s) => s.key === key) || {}).label || key;
}

export default function DestinationsSubSection() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("website:edit") || hasPermission("admin:all");

  const [slug, setSlug] = useState(SLUGS[0].value);  // default: curacao
  const [activeSection, setActiveSection] = useState("page_banner");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingSection, setSavingSection] = useState(null);

  const load = useCallback(async (newSlug) => {
    setLoading(true);
    try {
      const r = await adminApi.get(`/admin/destinations/${newSlug}`);
      setData(r.data || null);
    } catch (e) {
      toast.error(formatApiError(e, "Kon bestemming niet laden"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(slug); }, [load, slug]);

  const setField = (sec, k, v) => setData((d) => ({
    ...d,
    sections: { ...d.sections, [sec]: { ...(d.sections[sec] || {}), [k]: v } },
  }));
  const setListField = (sec, idx, k, v) => setData((d) => {
    const list = [...(d.sections[sec]?.items || [])];
    list[idx] = { ...(list[idx] || {}), [k]: v };
    return {
      ...d,
      sections: { ...d.sections, [sec]: { ...(d.sections[sec] || {}), items: list } },
    };
  });
  const addListItem = (sec, template) => setData((d) => {
    const list = [...(d.sections[sec]?.items || [])];
    list.push({ ...template });
    return {
      ...d,
      sections: { ...d.sections, [sec]: { ...(d.sections[sec] || {}), items: list } },
    };
  });
  const removeListItem = (sec, idx) => setData((d) => {
    const list = [...(d.sections[sec]?.items || [])];
    list.splice(idx, 1);
    return {
      ...d,
      sections: { ...d.sections, [sec]: { ...(d.sections[sec] || {}), items: list } },
    };
  });

  // Special handling for `page_banner.badges` (list within scalar section)
  const setBannerBadgeField = (idx, k, v) => setData((d) => {
    const banner = d.sections.page_banner || {};
    const list = [...(banner.badges || [])];
    list[idx] = { ...(list[idx] || {}), [k]: v };
    return {
      ...d,
      sections: { ...d.sections, page_banner: { ...banner, badges: list } },
    };
  });
  const addBannerBadge = () => setData((d) => {
    const banner = d.sections.page_banner || {};
    const list = [...(banner.badges || []), { icon: "shield", label: "" }];
    return {
      ...d,
      sections: { ...d.sections, page_banner: { ...banner, badges: list } },
    };
  });
  const removeBannerBadge = (idx) => setData((d) => {
    const banner = d.sections.page_banner || {};
    const list = [...(banner.badges || [])];
    list.splice(idx, 1);
    return {
      ...d,
      sections: { ...d.sections, page_banner: { ...banner, badges: list } },
    };
  });

  const saveSection = async (sec) => {
    if (!canEdit) return;
    setSavingSection(sec);
    try {
      const r = await adminApi.put(`/admin/destinations/${slug}/${sec}`, {
        payload: data.sections[sec] || {},
      });
      if (r.data) setData(r.data);
      toast.success(`${sectionLabel(sec)} opgeslagen`);
    } catch (e) {
      toast.error(formatApiError(e, "Opslaan mislukt"));
    } finally {
      setSavingSection(null);
    }
  };

  if (loading || !data) {
    return (
      <div className="py-10 text-center text-gray-400 text-sm">
        <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
        Bestemmingsinhoud laden...
      </div>
    );
  }

  const activeSec = data.sections[activeSection] || {};

  return (
    <div data-testid="destinations-sub-section">
      {!canEdit && (
        <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900">
          U heeft alleen leesrechten — wijzigen vereist Owner/Admin of Content Manager.
        </div>
      )}

      <div className="mb-4 px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-900 leading-relaxed">
        <strong>Belangrijke richtlijn:</strong> Vermijd harde transittijden of
        garanties (bv. "altijd binnen 7 dagen"). Wij verstrekken geen fiscaal
        advies; verwijs voor douane- en invoerinformatie naar de lokale
        autoriteiten. Noem geen concurrerende bedrijven op de website.
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {SLUGS.map((s) => {
          const active = slug === s.value;
          return (
            <button
              key={s.value}
              type="button"
              onClick={() => { setSlug(s.value); setActiveSection("page_banner"); }}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${active ? "bg-[#082c50] text-white" : "bg-slate-100 text-[#082c50] hover:bg-slate-200"}`}
              data-testid={`destination-slug-${s.value}`}
            >
              <MapPin className="w-4 h-4" /> {s.label}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-2 mb-4 border-b border-slate-200 pb-3">
        {SECTIONS.map((sec) => (
          <button
            key={sec.key}
            type="button"
            onClick={() => setActiveSection(sec.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${activeSection === sec.key ? "bg-[#082c50] text-white" : "bg-slate-100 text-[#082c50] hover:bg-slate-200"}`}
            data-testid={`destination-section-${sec.key}`}
          >
            {sec.label}
          </button>
        ))}
      </div>

      <SectionForm
        sectionKey={activeSection}
        values={activeSec}
        slug={slug}
        canEdit={canEdit}
        saving={savingSection === activeSection}
        onSave={() => saveSection(activeSection)}
        onChange={(k, v) => setField(activeSection, k, v)}
        onListChange={(idx, k, v) => setListField(activeSection, idx, k, v)}
        onListAdd={(tpl) => addListItem(activeSection, tpl)}
        onListRemove={(idx) => removeListItem(activeSection, idx)}
        onBannerBadgeChange={setBannerBadgeField}
        onBannerBadgeAdd={addBannerBadge}
        onBannerBadgeRemove={removeBannerBadge}
      />

      <p className="mt-4 text-xs text-gray-500 leading-relaxed">
        Wijzigingen verschijnen direct op de publieke /shipping-to-{slug}{" "}
        pagina. SEO-velden bepalen de browser-titel, meta-description en
        keywords + drijven het Service- en Breadcrumb-schema.
      </p>
    </div>
  );
}

function SectionForm({
  sectionKey, values, slug, canEdit, saving, onSave,
  onChange, onListChange, onListAdd, onListRemove,
  onBannerBadgeChange, onBannerBadgeAdd, onBannerBadgeRemove,
}) {
  return (
    <div
      className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4"
      data-testid={`destination-editor-${slug}-${sectionKey}`}
    >
      <div className="flex items-center justify-between">
        <h3 className="font-chivo font-bold text-lg text-[#082c50]">
          {sectionLabel(sectionKey)}
        </h3>
        <Button
          onClick={onSave}
          disabled={!canEdit || saving}
          className="rounded-xl bg-[#082c50] hover:bg-[#0a335c] text-white"
          data-testid={`destination-save-${sectionKey}`}
        >
          {saving
            ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Opslaan...</>
            : <><Save className="w-4 h-4 mr-1" />Opslaan</>}
        </Button>
      </div>

      {sectionKey === "page_banner" && (
        <>
          <TextRow label="Eyebrow" v={values.eyebrow} onChange={(v) => onChange("eyebrow", v)} testid={`${slug}-banner-eyebrow`} disabled={!canEdit} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <TextRow label="Hoofdtitel" v={values.title} onChange={(v) => onChange("title", v)} testid={`${slug}-banner-title`} disabled={!canEdit} />
            <TextRow label="Highlight (gradient)" v={values.highlight} onChange={(v) => onChange("highlight", v)} testid={`${slug}-banner-highlight`} disabled={!canEdit} />
          </div>
          <AreaRow label="Subtitle" v={values.subtitle} onChange={(v) => onChange("subtitle", v)} testid={`${slug}-banner-subtitle`} disabled={!canEdit} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <TextRow label="Primary CTA label" v={values.cta_primary_label} onChange={(v) => onChange("cta_primary_label", v)} disabled={!canEdit} />
            <TextRow label="Primary CTA URL" v={values.cta_primary_url} onChange={(v) => onChange("cta_primary_url", v)} disabled={!canEdit} />
            <TextRow label="Secondary CTA label" v={values.cta_secondary_label} onChange={(v) => onChange("cta_secondary_label", v)} disabled={!canEdit} />
            <TextRow label="Secondary CTA URL" v={values.cta_secondary_url} onChange={(v) => onChange("cta_secondary_url", v)} disabled={!canEdit} />
          </div>
          <BadgeListEditor
            items={values.badges || []}
            canEdit={canEdit}
            onChange={onBannerBadgeChange}
            onAdd={onBannerBadgeAdd}
            onRemove={onBannerBadgeRemove}
          />
        </>
      )}

      {sectionKey === "intro" && (
        <>
          <TextRow label="Eyebrow" v={values.eyebrow} onChange={(v) => onChange("eyebrow", v)} disabled={!canEdit} />
          <TextRow label="Sectietitel (H2)" v={values.title} onChange={(v) => onChange("title", v)} disabled={!canEdit} />
          <AreaRow label="Body tekst" v={values.body} onChange={(v) => onChange("body", v)} disabled={!canEdit} rows={8} />
          <p className="text-xs text-gray-500 italic">
            Voeg een lege regel toe (Enter, Enter) voor een nieuwe paragraaf.
            Behoud de algemene disclaimer onderaan — dit beschermt 5999Cargo
            tegen claims over harde transittijden of douaneregels.
          </p>
        </>
      )}

      {sectionKey === "service_blocks" && (
        <ListEditor
          label="Service-blokken op deze bestemming (max 8)"
          items={values.items || []}
          fields={[
            { key: "icon",        label: "Icon",       type: "select", options: ICON_OPTIONS },
            { key: "title",       label: "Titel",      type: "text" },
            { key: "description", label: "Beschrijving", type: "textarea" },
            { key: "link_label",  label: "Link label", type: "text" },
            { key: "link_url",    label: "Link URL",   type: "text" },
          ]}
          template={{ icon: "ship", title: "", description: "", link_label: "", link_url: "" }}
          onChange={onListChange}
          onAdd={onListAdd}
          onRemove={onListRemove}
          canEdit={canEdit}
          max={8}
        />
      )}

      {sectionKey === "faq" && (
        <ListEditor
          label="FAQ items (max 12)"
          items={values.items || []}
          fields={[
            { key: "question", label: "Vraag",    type: "text" },
            { key: "answer",   label: "Antwoord", type: "textarea" },
          ]}
          template={{ question: "", answer: "" }}
          onChange={onListChange}
          onAdd={onListAdd}
          onRemove={onListRemove}
          canEdit={canEdit}
          max={12}
        />
      )}

      {sectionKey === "process_steps" && (
        <ListEditor
          label="Stappenplan-items (aanbevolen 5, max 8)"
          items={values.items || []}
          fields={[
            { key: "icon",        label: "Icon",         type: "select", options: ICON_OPTIONS },
            { key: "title",       label: "Stap-titel",   type: "text" },
            { key: "description", label: "Beschrijving", type: "textarea" },
          ]}
          template={{ icon: "package", title: "", description: "" }}
          onChange={onListChange}
          onAdd={onListAdd}
          onRemove={onListRemove}
          canEdit={canEdit}
          max={8}
        />
      )}

      {sectionKey === "trust_signals" && (
        <ListEditor
          label="Trust signal items (max 8)"
          items={values.items || []}
          fields={[
            { key: "icon",        label: "Icon",         type: "select", options: ICON_OPTIONS },
            { key: "title",       label: "Titel",        type: "text" },
            { key: "description", label: "Beschrijving", type: "textarea" },
          ]}
          template={{ icon: "shield", title: "", description: "" }}
          onChange={onListChange}
          onAdd={onListAdd}
          onRemove={onListRemove}
          canEdit={canEdit}
          max={8}
        />
      )}

      {sectionKey === "cta_band" && (
        <>
          <TextRow label="Titel" v={values.title} onChange={(v) => onChange("title", v)} disabled={!canEdit} />
          <AreaRow label="Beschrijving" v={values.description} onChange={(v) => onChange("description", v)} disabled={!canEdit} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <TextRow label="Primary CTA label" v={values.cta_primary_label} onChange={(v) => onChange("cta_primary_label", v)} disabled={!canEdit} />
            <TextRow label="Primary CTA URL" v={values.cta_primary_url} onChange={(v) => onChange("cta_primary_url", v)} disabled={!canEdit} />
            <TextRow label="Secondary CTA label" v={values.cta_secondary_label} onChange={(v) => onChange("cta_secondary_label", v)} disabled={!canEdit} />
            <TextRow label="Secondary CTA URL" v={values.cta_secondary_url} onChange={(v) => onChange("cta_secondary_url", v)} disabled={!canEdit} />
          </div>
          <p className="text-xs text-gray-500 italic">
            Laat de titel leeg om de hele CTA-band te verbergen.
          </p>
        </>
      )}

      {sectionKey === "seo" && (
        <>
          <TextRow label="Browser titel (meta title)" v={values.title} onChange={(v) => onChange("title", v)} disabled={!canEdit} testid={`${slug}-seo-title`} />
          <AreaRow label="Meta description" v={values.description} onChange={(v) => onChange("description", v)} disabled={!canEdit} rows={3} testid={`${slug}-seo-description`} />
          <AreaRow label="Keywords (comma-separated)" v={values.keywords} onChange={(v) => onChange("keywords", v)} disabled={!canEdit} rows={2} testid={`${slug}-seo-keywords`} />
          <p className="text-xs text-gray-500 italic">
            Houd titel onder ~60 tekens en description onder ~155 tekens
            voor optimale weergave in Google.
          </p>
        </>
      )}
    </div>
  );
}

function TextRow({ label, v, onChange, testid, disabled }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input
        value={v ?? ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        data-testid={testid ? `${testid}-input` : undefined}
      />
    </div>
  );
}

function AreaRow({ label, v, onChange, testid, disabled, rows = 3 }) {
  return (
    <div>
      <Label>{label}</Label>
      <Textarea
        rows={rows}
        value={v ?? ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        data-testid={testid ? `${testid}-input` : undefined}
      />
    </div>
  );
}

function BadgeListEditor({ items, canEdit, onChange, onAdd, onRemove }) {
  return (
    <div className="border border-slate-200 rounded-xl p-3 bg-slate-50/50">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold text-[#082c50]">
          USP-badges (max 5)
        </span>
        {canEdit && items.length < 5 && (
          <Button size="sm" variant="outline" onClick={onAdd} className="rounded-lg text-xs">
            <Plus className="w-3 h-3 mr-1" /> Toevoegen
          </Button>
        )}
      </div>
      <div className="space-y-2">
        {items.map((it, idx) => (
          <div key={idx} className="flex flex-wrap items-end gap-2 p-2 bg-white rounded-lg border border-slate-200">
            <div className="flex-1 min-w-[140px]">
              <Label className="text-[10px] uppercase tracking-wide">Icon</Label>
              <select
                value={it.icon || ""}
                onChange={(e) => onChange(idx, "icon", e.target.value)}
                disabled={!canEdit}
                className="w-full h-9 px-2 rounded-lg border border-slate-200 text-sm"
              >
                {ICON_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div className="flex-1 min-w-[180px]">
              <Label className="text-[10px] uppercase tracking-wide">Label</Label>
              <Input
                value={it.label || ""}
                onChange={(e) => onChange(idx, "label", e.target.value)}
                disabled={!canEdit}
              />
            </div>
            {canEdit && (
              <Button size="sm" variant="outline" onClick={() => onRemove(idx)} className="rounded-lg text-red-600">
                <Trash2 className="w-3 h-3" />
              </Button>
            )}
          </div>
        ))}
        {items.length === 0 && (
          <div className="text-xs text-gray-500 italic py-2">Nog geen badges.</div>
        )}
      </div>
    </div>
  );
}

function ListEditor({ label, items, fields, template, onChange, onAdd, onRemove, canEdit, max }) {
  return (
    <div className="border border-slate-200 rounded-xl p-3 bg-slate-50/50">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold text-[#082c50]">
          {label} <span className="text-gray-400 font-normal">({items.length}/{max})</span>
        </span>
        {canEdit && items.length < (max || 12) && (
          <Button size="sm" variant="outline" onClick={() => onAdd(template)} className="rounded-lg text-xs">
            <Plus className="w-3 h-3 mr-1" /> Toevoegen
          </Button>
        )}
      </div>
      <div className="space-y-3">
        {items.map((it, idx) => (
          <div key={idx} className="p-3 bg-white rounded-lg border border-slate-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500">Item #{idx + 1}</span>
              {canEdit && (
                <Button size="sm" variant="outline" onClick={() => onRemove(idx)} className="rounded-lg text-red-600 h-7 px-2">
                  <Trash2 className="w-3 h-3" />
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {fields.map((f) => (
                <div key={f.key} className={f.type === "textarea" ? "md:col-span-2" : ""}>
                  <Label className="text-[10px] uppercase tracking-wide">{f.label}</Label>
                  {f.type === "select" ? (
                    <select
                      value={it[f.key] || ""}
                      onChange={(e) => onChange(idx, f.key, e.target.value)}
                      disabled={!canEdit}
                      className="w-full h-9 px-2 rounded-lg border border-slate-200 text-sm"
                    >
                      {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : f.type === "textarea" ? (
                    <Textarea
                      rows={3}
                      value={it[f.key] || ""}
                      onChange={(e) => onChange(idx, f.key, e.target.value)}
                      disabled={!canEdit}
                    />
                  ) : (
                    <Input
                      value={it[f.key] || ""}
                      onChange={(e) => onChange(idx, f.key, e.target.value)}
                      disabled={!canEdit}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="text-xs text-gray-500 italic py-2">Nog geen items.</div>
        )}
      </div>
    </div>
  );
}
